/**
 * The wrapper every upgrade callback goes through.
 *
 * IndexedDB gives this phase something it rarely gets for free: a version-change
 * transaction is atomic, so an upgrade that throws leaves the database exactly
 * as it was, still at the old version, still holding every row. That is the
 * whole mechanism behind §22's "must refuse to run" — there is nothing to undo
 * by hand, and no half-migrated state to reason about. What was missing was
 * anything that checks.
 *
 * So each upgrade is bracketed by a census (`census.ts`). The work at risk is
 * counted after Dexie has created the version's stores and before the callback
 * touches a row, the callback runs, and the same count is taken again. Any
 * store that came out lower throws `MigrationRefused`, the transaction aborts,
 * and this morning's database is still this morning's database.
 *
 * Deliberately *not* a check that the migration is correct. It cannot be: a
 * migration that rewrites every mutation's values into nonsense passes this
 * cleanly, because the count is the same. It catches the one failure mode that
 * is both plausible and unrecoverable — a `modify` with a bad predicate, a
 * `bulkPut` that collapses keys, a `delete` that was meant to be narrower — and
 * says so with the numbers rather than with a stack trace.
 */
import type { Transaction } from "dexie"

import { countDurableWork, shortfallsBetween } from "./census"
import { MigrationRefused } from "./refusal"

export type UpgradeCallback = (transaction: Transaction) => Promise<void>

/**
 * Wrap an upgrade so it cannot commit a loss.
 *
 * `version` is passed rather than inferred because the refusal names it, and the
 * number an operator reads has to be the number in the `.version()` call above
 * it. Dexie exposes no reliable way to ask a transaction which upgrade it is
 * running.
 */
export function preserving(version: number, upgrade: UpgradeCallback): UpgradeCallback {
  return async transaction => {
    const before = await countDurableWork(transaction)

    await upgrade(transaction)

    const after = await countDurableWork(transaction)
    const shortfalls = shortfallsBetween(before, after)

    if (shortfalls.length > 0) throw new MigrationRefused(version, shortfalls)
  }
}
